import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { format } from "date-fns";
import { styled } from "@material-ui/styles";

import DatePicker from "../../../elements/DatePicker";
import { CAMPAIGN_PLAY_TYPE, CAMPAIGN_PLAY_TYPE_LABELS } from "../../../../constants/campaignPlayTypes";

export type CampaignFilters = {
  search: string;
  displayStatus: string;
  campaignType: string;
  startDateFrom: string;
  endDateTo: string;
};

type CampaignsSectionToolbarProps = {
  advertiserName: string;
  filters: CampaignFilters;
  onFilterChange: (key: keyof CampaignFilters, value: string) => void;
  onCreateCampaign: () => void;
};

const CAMPAIGN_TYPE_OPTIONS = Object.entries(CAMPAIGN_PLAY_TYPE_LABELS) as [CAMPAIGN_PLAY_TYPE, string][];

const DISPLAY_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
  { value: "processing", label: "Processing" },
];

const Root = styled("div")({ marginBottom: 16 });

const TitleRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
});

const SectionTitle = styled("h2")(({ theme }: { theme: Theme }) => ({
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const FilterRow = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "flex-end",
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 160,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: theme.palette.brand.gray500,
}));

const InputSkin = styled("input")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  boxSizing: "border-box" as const,
  width: "100%",
  fontFamily: "inherit",
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
  "&::placeholder": { color: theme.palette.brand.gray500 },
}));

const StyledSelect = styled(Select)(({ theme }: { theme: Theme }) => ({
  height: 37,
  background: theme.palette.brand.white,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  width: "100%",
  "& .MuiSelect-select": {
    padding: "0 32px 0 12px",
    height: "100%",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box" as const,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${theme.palette.brand.gray200}`,
    borderRadius: 8,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.brand.gray300,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${theme.palette.brand.blueAccent}`,
  },
}));

const DROPDOWN_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  getContentAnchorEl: null,
};

const DatePickerWrap = styled("div")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: 37,
    background: theme.palette.brand.white,
    borderRadius: 8,
    fontSize: 13,
    color: theme.palette.brand.gray900,
  },
  "& .MuiOutlinedInput-root fieldset": {
    border: `1px solid ${theme.palette.brand.gray200}`,
    borderRadius: 8,
  },
  "& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: theme.palette.brand.gray300,
  },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    border: `1px solid ${theme.palette.brand.blueAccent}`,
  },
  "& .MuiInputBase-input": {
    padding: "8px 12px",
    fontSize: 13,
  },
  "& label": { display: "none" },
  "& .MuiFormHelperText-root": { display: "none" },
  "& .MuiInputAdornment-root .MuiIconButton-root": {
    color: theme.palette.brand.gray500,
    padding: 4,
  },
}));

const CampaignsSectionToolbar: FC<CampaignsSectionToolbarProps> = ({
  advertiserName,
  filters,
  onFilterChange,
  onCreateCampaign,
}) => (
  <Root>
    <TitleRow>
      <SectionTitle>{advertiserName} Campaigns</SectionTitle>
    </TitleRow>
    <FilterRow>
      <FieldGroup style={{ minWidth: 220 }}>
        <FieldLabel>Search</FieldLabel>
        <InputSkin
          type="text"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>Status</FieldLabel>
        <StyledSelect
          variant="outlined"
          displayEmpty
          MenuProps={DROPDOWN_MENU_PROPS}
          value={filters.displayStatus}
          onChange={(e) => onFilterChange("displayStatus", e.target.value as string)}
        >
          <MenuItem value="">All</MenuItem>
          {DISPLAY_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </FieldGroup>

      <FieldGroup style={{ minWidth: 200 }}>
        <FieldLabel>Campaign Type</FieldLabel>
        <StyledSelect
          variant="outlined"
          displayEmpty
          MenuProps={DROPDOWN_MENU_PROPS}
          value={filters.campaignType}
          onChange={(e) => onFilterChange("campaignType", e.target.value as string)}
        >
          <MenuItem value="">All</MenuItem>
          {CAMPAIGN_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </StyledSelect>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>Start Date From</FieldLabel>
        <DatePickerWrap>
          <DatePicker
            label=""
            value={filters.startDateFrom ? new Date(`${filters.startDateFrom}T12:00:00`) : null}
            onChange={(date) => onFilterChange("startDateFrom", date ? format(date as Date, "yyyy-MM-dd") : "")}
            renderInput={(_params) => <span />}
          />
        </DatePickerWrap>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>End Date To</FieldLabel>
        <DatePickerWrap>
          <DatePicker
            label=""
            value={filters.endDateTo ? new Date(`${filters.endDateTo}T12:00:00`) : null}
            onChange={(date) => onFilterChange("endDateTo", date ? format(date as Date, "yyyy-MM-dd") : "")}
            renderInput={(_params) => <span />}
          />
        </DatePickerWrap>
      </FieldGroup>
    </FilterRow>
  </Root>
);

export default CampaignsSectionToolbar;
